import { Fragment, useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '@/api/client';
import { Pager } from '@/components/Pager';
import type { PickupFileSummary, PickupDeliveryDto } from '@/types';
import {
  formatBytes,
  formatRows,
  monthRange,
  saveBlob,
  describeRenderIdentity,
  describeLocation,
  describeStraddledYear
} from '@/components/pickupFormat';
import { HelpLink } from '@/components/HelpLink';

const pageSize = 50;

/**
 * Report pickup — the commercial team's view of everything scheduled subscriptions have
 * delivered to file storage.
 *
 * The page is built around one journey: "get me last month's Manchester file". That is why the
 * client is reached through the free-text search rather than a picker of its own, why "Last month"
 * is a one-click date preset, and why the filters live in the query string — a filtered view has to
 * survive a refresh and be pasteable to a colleague.
 */
export function ReportPickup() {
  const api = useApi();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();

  // Read-only now. The chip bar that used to set this is gone — there are thousands of clients, so
  // narrowing by client is the search box's job. The param is still honoured on the way IN so that
  // a bookmarked or pasted ?client= URL keeps filtering exactly as it did, and "Clear filters"
  // still counts it so whoever lands on one can get back to the full list.
  const clientKey = params.get('client') ?? '';
  const format = params.get('format') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const currentYearOnly = params.get('year') === 'current';
  const renderedAs = params.get('renderedAs') ?? '';

  // Search is local state mirrored into the URL after a debounce: putting every keystroke in
  // the query string makes the back button useless.
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [skip, setSkip] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => setSkip(0), [clientKey, format, from, to, currentYearOnly, renderedAs, debouncedSearch]);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    // replace so filtering doesn't fill the history stack with every intermediate state.
    setParams(next, { replace: true });
  };

  const { data: status } = useQuery({ queryKey: ['pickup-status'], queryFn: api.pickupStatus });

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['pickup-files', clientKey, format, from, to, currentYearOnly, renderedAs, debouncedSearch, skip],
    queryFn: () => api.pickupSearch({
      search: debouncedSearch || undefined,
      clientKey: clientKey || undefined,
      format: format || undefined,
      from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
      to: to ? new Date(`${to}T23:59:59`).toISOString() : undefined,
      currentYearOnly: currentYearOnly || undefined,
      renderedAs: renderedAs || undefined,
      skip,
      take: pageSize
    }),
    placeholderData: keepPreviousData
  });

  const { data: deliveries } = useQuery({
    queryKey: ['pickup-deliveries'],
    queryFn: api.pickupDeliveries
  });

  const download = useMutation({
    mutationFn: async (file: PickupFileSummary) => {
      const { blob, fileName } = await api.pickupDownload(file.id);
      saveBlob(blob, fileName || file.fileName);
      return file.id;
    },
    onSuccess: () => {
      setDownloadError(null);
      // The download counter and last-downloaded time move on every download, so the list is
      // refetched rather than left showing figures that are now one behind.
      void queryClient.invalidateQueries({ queryKey: ['pickup-files'] });
    },
    onError: (e: Error) => setDownloadError(e.message.replace(/^API \d+: /, ''))
  });

  const files = data?.files ?? [];
  const total = data?.total ?? 0;
  const formats = data?.formats ?? [];
  const renderIdentities = data?.renderedAs ?? [];

  const hasFilters = Boolean(clientKey || format || from || to || search || currentYearOnly || renderedAs);

  if (error) {
    return <div className="error-banner">{(error as Error).message.replace(/^API \d+: /, '')}</div>;
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Report pickup</h1>
          <div className="sub">
            Scheduled reports delivered to file storage. Every download is recorded.{' '}
            <HelpLink slug="find-a-pickup-file" />
          </div>
        </div>
      </div>

      {status && !status.isConfigured && (
        <div className="notice notice-warn">
          <strong>Pickup storage is not configured yet.</strong> Once the storage account is
          deployed and the report server is pointed at it, delivered files appear here
          automatically. Nothing needs changing in the portal.
        </div>
      )}

      {downloadError && <div className="error-banner">{downloadError}</div>}

      <div className="list-filters" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <FilterLabel label="Search">
          <input
            className="input"
            style={{ minWidth: 240 }}
            placeholder="Report, client or file name"
            value={search}
            onChange={e => { setSearch(e.target.value); setParam('q', e.target.value.trim()); }}
          />
        </FilterLabel>

        <FilterLabel label="From">
          <input className="input" type="date" value={from} onChange={e => setParam('from', e.target.value)} />
        </FilterLabel>

        <FilterLabel label="To">
          <input className="input" type="date" value={to} onChange={e => setParam('to', e.target.value)} />
        </FilterLabel>

        <FilterLabel label="Format">
          <select className="select" value={format} onChange={e => setParam('format', e.target.value)}>
            <option value="">All formats</option>
            {formats.map(f => <option key={f.key} value={f.key}>{f.label} ({f.count})</option>)}
          </select>
        </FilterLabel>

        <div style={{ display: 'flex', gap: 6 }}>
          {/* The single commonest request this page exists to serve, so it is one click. */}
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => {
              const [f, t] = monthRange(-1);
              const next = new URLSearchParams(params);
              next.set('from', f); next.set('to', t);
              setParams(next, { replace: true });
            }}
          >
            Last month
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => {
              const [f, t] = monthRange(0);
              const next = new URLSearchParams(params);
              next.set('from', f); next.set('to', t);
              setParams(next, { replace: true });
            }}
          >
            This month
          </button>
        </div>

        {/* Rendered-as is only meaningful for KT Insights files, so the filter appears only when
            there are some. It matters because the recipient's identity decides what is IN the
            file, not merely who it was for. */}
        {renderIdentities.length > 0 && (
          <FilterLabel label="Rendered as">
            <select
              className="select"
              style={{ minWidth: 220 }}
              value={renderedAs}
              onChange={e => setParam('renderedAs', e.target.value)}
            >
              <option value="">Anyone</option>
              {renderIdentities.map(r => (
                <option key={r.key} value={r.key}>{r.label} ({r.count})</option>
              ))}
            </select>
          </FilterLabel>
        )}

        {/* Prior years are ordinary results by default — a year folder is where a file lives, not
            a state it is in. This is the opposite journey: "just show me this year". */}
        <label className="checkbox-field" style={{ alignSelf: 'center' }}>
          <input
            type="checkbox"
            checked={currentYearOnly}
            onChange={e => setParam('year', e.target.checked ? 'current' : '')}
          />
          This year only
        </label>

        {hasFilters && (
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={() => { setSearch(''); setParams(new URLSearchParams(), { replace: true }); }}
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="card">
        {isLoading ? (
          <div className="placeholder">Loading…</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Report</th>
                <th>Client</th>
                <th>Period</th>
                <th>Rendered as</th>
                <th>Produced</th>
                <th>Size</th>
                <th>Rows</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {files.length === 0 && (
                <tr>
                  <td colSpan={8} className="placeholder">
                    {hasFilters
                      ? 'No files match these filters.'
                      : 'No files have been delivered to pickup yet.'}
                  </td>
                </tr>
              )}
              {files.map(file => (
                <Fragment key={file.id}>
                  <tr
                    onClick={() => setExpanded(expanded === file.id ? null : file.id)}
                    aria-expanded={expanded === file.id}
                  >
                    <td>
                      <div className="report-name">{file.reportName}</div>
                      <div className="report-path">{file.fileName}</div>
                    </td>
                    <td>
                      {file.clientName ?? <span className="run-param-blank">Unfiled</span>}
                      {/* The id is what makes a client unambiguous — names are neither unique
                          nor stable, and two clients share a name more often than you would think. */}
                      {file.headOfficeId && <div className="report-path">{file.headOfficeId}</div>}
                    </td>
                    <td>
                      {file.periodLabel ?? '—'}
                      {/* Only when the year folder and the period disagree — a December run that
                          fired in January. Every other row would just repeat the period's year. */}
                      {describeStraddledYear(file.yearFolder, file.periodKey) && (
                        <div className="report-path">
                          {describeStraddledYear(file.yearFolder, file.periodKey)}
                        </div>
                      )}
                    </td>
                    <td><RenderedAsCell file={file} /></td>
                    <td>{new Date(file.producedUtc).toLocaleString()}</td>
                    <td>{formatBytes(file.sizeBytes)}</td>
                    {/* Row count comes from the report server's execution log; an em dash means
                        the run could not be matched, which is different from "zero rows". */}
                    <td>{formatRows(file.rowCount)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {file.state === 'Available' ? (
                        <button
                          type="button"
                          className="btn-primary btn-sm"
                          disabled={download.isPending}
                          onClick={e => { e.stopPropagation(); download.mutate(file); }}
                        >
                          {download.isPending && download.variables?.id === file.id ? 'Preparing…' : 'Download'}
                        </button>
                      ) : (
                        // Never "Expired" — the portal does not delete, so a file that is not
                        // there was removed from the share by hand.
                        <span className="pill pill-stale" title="Indexed, but no longer on the pickup share">
                          Missing
                        </span>
                      )}
                    </td>
                  </tr>
                  {expanded === file.id && (
                    <tr className="run-detail-row">
                      <td colSpan={8}>
                        <FileDetail file={file} deliveries={deliveries ?? []} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pager
        skip={skip}
        take={pageSize}
        shown={files.length}
        total={total}
        busy={isFetching}
        onChange={setSkip}
      />
    </>
  );
}

function FilterLabel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="field" style={{ margin: 0 }}>
      <label>{label}</label>
      {children}
    </div>
  );
}

/**
 * Whose view of the data this file is.
 *
 * A column of its own rather than a line buried in the detail panel, because it is not metadata
 * about the delivery — it decides what is IN the file. A KT Insights export renders as its
 * recipient: a row-level filter is bound to that person's email for every role except Internal,
 * and per-user page permissions decide which pages are included at all. Someone scanning this
 * list for a file to forward to a customer has to see it without opening anything.
 */
function RenderedAsCell({ file }: { file: PickupFileSummary }) {
  const identity = describeRenderIdentity(file);

  if (identity.tone === 'none') {
    return <span className="run-param-blank" title={identity.detail}>—</span>;
  }

  // Stated, never blank. A blank cell reads as "not applicable" when what it means is
  // "nobody could confirm what is in this file".
  if (identity.tone === 'unknown') {
    return <span className="pill pill-stale" title={identity.detail}>Unknown</span>;
  }

  return (
    <span title={identity.detail}>
      <span className={`pill ${identity.tone === 'internal' ? 'pill-disabled' : 'pill-stale'}`}>
        {identity.tone === 'internal' ? 'Internal' : 'Customer'}
      </span>
      <div className="report-path">{file.renderedAsName ?? file.renderedAsEmail}</div>
    </span>
  );
}

function FileDetail({ file, deliveries }: { file: PickupFileSummary; deliveries: PickupDeliveryDto[] }) {
  const delivery = deliveries.find(d => d.id === file.deliveryId);
  const identity = describeRenderIdentity(file);

  return (
    <div className="run-detail">
      <div className="run-detail-title">{file.fileName}</div>
      <dl className="run-params">
        <dt>Format</dt>
        <dd className="run-param-value">{file.formatLabel}</dd>

        <dt>Produced</dt>
        <dd className="run-param-value">{new Date(file.producedUtc).toLocaleString()}</dd>

        <dt>Size</dt>
        <dd className="run-param-value">{formatBytes(file.sizeBytes)}</dd>

        <dt>Rows</dt>
        <dd className="run-param-value">{formatRows(file.rowCount)}</dd>

        <dt>Source</dt>
        <dd className="run-param-value">
          {file.producer === 'KtInsights' ? 'KT Insights export' : 'Report server subscription'}
          {file.reportPath ? ` · ${file.reportPath}` : ''}
        </dd>

        {/* Spelled out in full here, because the consequence — what data this file actually
            contains — is the thing somebody needs before they forward it to anyone. */}
        <dt>Rendered as</dt>
        <dd className="run-param-value">
          {identity.text}
          <div className="sub">{identity.detail}</div>
        </dd>

        {/* The run that produced these exact bytes. Copied into the index at ingestion because
            the report server purges its execution log after 60 days. */}
        <dt>Produced by run</dt>
        <dd className="run-param-value">
          {file.runExecutionId
            ? <>{file.runExecutionId}{file.runStatus ? ` · ${file.runStatus}` : ''}</>
            : <span className="run-param-blank">Could not be matched to a run</span>}
        </dd>

        <dt>Downloads</dt>
        <dd className="run-param-value">
          {file.downloadCount === 0
            ? 'Never downloaded'
            : `${file.downloadCount} · last ${new Date(file.lastDownloadedUtc!).toLocaleString()}`}
        </dd>

        {/* Not "retained until". Nothing in pickup is ever deleted or moved — a file stays in the
            year folder it was delivered to, downloadable and audited, for good. */}
        <dt>Location</dt>
        <dd className="run-param-value">{describeLocation(file.yearFolder)}</dd>

        {delivery && (
          <>
            {/* The label follows the date rather than the other way round. An overdue delivery
                holds on to the occurrence it MISSED — that is what makes it overdue, and moving
                it on would be the thing that hid the missed run — so the date here is in the past
                whenever the flag is set, and calling it "next" would read as a future delivery
                that is already arranged. */}
            <dt>{delivery.isOverdue ? 'File expected' : 'Next file due'}</dt>
            <dd className="run-param-value">
              {delivery.expectedByUtc
                ? new Date(delivery.expectedByUtc).toLocaleString()
                : <span className="run-param-blank">No schedule recorded</span>}
              {delivery.isOverdue && ' · never arrived'}
            </dd>
          </>
        )}
      </dl>
      <div className="run-detail-note">
        Downloading this file records who took it, when, and which run it came from.
      </div>
    </div>
  );
}
